import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { WorkTypeModel } from "../../../../factories/work-type/models/work-type.model";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useActivateWorkTypeMutation,
  useDeactivateWorkTypeMutation,
  useDeleteWorkTypeMutation,
  useLazyGetWorkTypesQuery,
} from "../../../../slices/WorkTypeSlice";
import { serverErrorMessage } from "../model/workTypeEditor.utils";
import {
  WorkTypeListStatus,
  WorkTypesListController,
} from "../model/workTypesList.types";

export const useWorkTypesListController = (
  initialStatus: WorkTypeListStatus = "all",
): WorkTypesListController => {
  const navigate = useNavigate();

  const [activeStatus, setActiveStatus] =
    useState<WorkTypeListStatus>(initialStatus);
  const [openWorkTypeDelete, setOpenWorkTypeDelete] =
    useState<WorkTypeModel | null>(null);
  const [openWorkTypeDeactivate, setOpenWorkTypeDeactivate] =
    useState<WorkTypeModel | null>(null);
  const [openWorkTypeActivate, setOpenWorkTypeActivate] =
    useState<WorkTypeModel | null>(null);

  const [getWorkTypes, { isLoading: isLoadingWorkTypes, data: workTypes }] =
    useLazyGetWorkTypesQuery();
  const [deleteWorkType, { isLoading: isLoadingDelete }] =
    useDeleteWorkTypeMutation();
  const [deactivateWorkType, { isLoading: isLoadingDeactivate }] =
    useDeactivateWorkTypeMutation();
  const [activateWorkType, { isLoading: isLoadingActivate }] =
    useActivateWorkTypeMutation();

  const orgId = getLinkedFirm()?.orgId || "";

  const fetchWorkTypes = useCallback(async () => {
    if (!orgId) {
      return;
    }

    await getWorkTypes({
      orgId,
      status: activeStatus,
    }).unwrap();
  }, [activeStatus, getWorkTypes, orgId]);

  const handleStatusChange = useCallback((status: WorkTypeListStatus) => {
    setActiveStatus(status);
  }, []);

  const handleCreate = useCallback(() => {
    navigate("/settings/work-types/view/add");
  }, [navigate]);

  const handleEdit = useCallback(
    (workType: WorkTypeModel) => {
      navigate(`/settings/work-types/view/${workType.id}`);
    },
    [navigate],
  );

  const handleDeleteWorkType = useCallback(
    async (workType: WorkTypeModel) => {
      try {
        await deleteWorkType({ id: workType.id }).unwrap();
        await fetchWorkTypes();
        toast("Work type successfully deleted", {
          type: "success",
        });
        setOpenWorkTypeDelete(null);
      } catch (error) {
        toast(
          serverErrorMessage(
            error,
            "Something went wrong. You can deactivate the work type to hide it",
          ),
          {
            type: "error",
          },
        );
      }
    },
    [deleteWorkType, fetchWorkTypes],
  );

  const handleDeactivateWorkType = useCallback(
    async (workType: WorkTypeModel) => {
      try {
        await deactivateWorkType({ id: workType.id }).unwrap();
        await fetchWorkTypes();
        toast("Work type successfully deactivated", {
          type: "success",
        });
        setOpenWorkTypeDeactivate(null);
      } catch {
        toast("Something went wrong", {
          type: "error",
        });
      }
    },
    [deactivateWorkType, fetchWorkTypes],
  );

  const handleActivateWorkType = useCallback(
    async (workType: WorkTypeModel) => {
      try {
        await activateWorkType({ id: workType.id }).unwrap();
        await fetchWorkTypes();
        toast("Work type successfully activated", {
          type: "success",
        });
        setOpenWorkTypeActivate(null);
      } catch {
        toast("Something went wrong", {
          type: "error",
        });
      }
    },
    [activateWorkType, fetchWorkTypes],
  );

  useEffect(() => {
    void fetchWorkTypes();
  }, [fetchWorkTypes]);

  useEffect(() => {
    setActiveStatus(initialStatus);
  }, [initialStatus]);

  return {
    headerProps: {
      activeStatus,
      onStatusChange: handleStatusChange,
      onCreate: handleCreate,
    },
    tableProps: {
      isLoading: isLoadingWorkTypes,
      workTypes: workTypes || [],
      onEdit: handleEdit,
      onDeactivate: setOpenWorkTypeDeactivate,
      onActivate: setOpenWorkTypeActivate,
      onDelete: setOpenWorkTypeDelete,
    },
    modalProps: {
      deleteWorkType: openWorkTypeDelete,
      deactivateWorkType: openWorkTypeDeactivate,
      activateWorkType: openWorkTypeActivate,
      isDeleting: isLoadingDelete,
      isDeactivating: isLoadingDeactivate,
      isActivating: isLoadingActivate,
      onCloseDelete: () => setOpenWorkTypeDelete(null),
      onCloseDeactivate: () => setOpenWorkTypeDeactivate(null),
      onCloseActivate: () => setOpenWorkTypeActivate(null),
      onConfirmDelete: () =>
        openWorkTypeDelete && void handleDeleteWorkType(openWorkTypeDelete),
      onConfirmDeactivate: () =>
        openWorkTypeDeactivate &&
        void handleDeactivateWorkType(openWorkTypeDeactivate),
      onConfirmActivate: () =>
        openWorkTypeActivate &&
        void handleActivateWorkType(openWorkTypeActivate),
    },
  };
};
